'use es6';

import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { sortBy } from 'underscore';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentRoutes from 'ContentUtils/Routes';
import UIIcon from 'UIComponents/icon/UIIcon';
import UISelect from 'UIComponents/input/UISelect';
import { EERIE } from 'HubStyleTokens/colors';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getCurrentTranslations, getHasCurrentTranslations, getSelectedContentId } from 'ContentEditorUI/redux/selectors/translationSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getLanguageNameFromKey, isMasterTranslation } from 'ContentEditorUI/utils/multiLangUtils';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getTranslatedFromId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getActiveDomainName } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getSiteSettingsBodyOverrideByDomain } from 'ContentEditorUI/redux/selectors/siteSettingsSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getRenderLanguageForActiveDomain(bodyOverrideByDomain, activeDomainName) {
  if (!bodyOverrideByDomain) {
    return null;
  }
  return bodyOverrideByDomain[activeDomainName] && bodyOverrideByDomain[activeDomainName]['contentLanguage'] || bodyOverrideByDomain[activeDomainName] && bodyOverrideByDomain[activeDomainName]['content_language'];
}
function getIsPrimaryLanguageVariant(language, translation, renderLanguageForActiveDomain) {
  return renderLanguageForActiveDomain ? renderLanguageForActiveDomain === language : isMasterTranslation(translation);
}
const TranslationSelectorContainer = ({
  className
}) => {
  const contentId = useSelector(getSelectedContentId);
  const categoryId = useSelector(getCategoryId);
  const hasCurrentTranslations = useSelector(getHasCurrentTranslations);
  const translations = useSelector(getCurrentTranslations);
  const translatedFromId = useSelector(getTranslatedFromId);
  const activeDomainName = useSelector(getActiveDomainName);
  const bodyOverrideByDomain = useSelector(getSiteSettingsBodyOverrideByDomain);
  const renderLanguageForActiveDomain = getRenderLanguageForActiveDomain(bodyOverrideByDomain, activeDomainName);
  if (!hasCurrentTranslations) {
    return null;
  }
  const onChangeTranslation = e => {
    if (contentId !== e.target.value) {
      UsageTracker.trackAsBeacon('editorInteraction', {
        action: 'Multilanguage Action',
        event: 'switched-translation',
        isLanguageVariation: !!translatedFromId
      });
      location.href = ContentRoutes.edit(e.target.value, categoryId);
    }
  };
  const TestIconWithTooltip = () => /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "translationSelector.iconTooltip"
    }),
    placement: "right",
    children: /*#__PURE__*/_jsx(UIIcon, {
      name: "test",
      color: EERIE
    })
  });
  function getTranslationOption(translation) {
    const {
      id,
      language,
      ab,
      mab
    } = translation;
    const languageName = getLanguageNameFromKey(language);
    const isPrimaryLanguageVariant = getIsPrimaryLanguageVariant(language, translation, renderLanguageForActiveDomain);
    return {
      text: isPrimaryLanguageVariant ? I18n.text('translationSelector.masterLanguage', {
        language: languageName
      }) : languageName,
      value: id,
      badge: (ab || mab) && /*#__PURE__*/_jsx(TestIconWithTooltip, {})
    };
  }
  const getTranslationOptions = () => sortBy(translations.map(getTranslationOption), 'text');
  function makeButtonTextRenderer() {
    const translation = translations.find(({
      id
    }) => id === contentId);
    const {
      language
    } = translation;
    return () => /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "language",
        className: "m-right-1"
      }), getLanguageNameFromKey(language)]
    });
  }
  const translationSelector = /*#__PURE__*/_jsx(UISelect, {
    "data-onboarding": "translation-variation-selector",
    className: "p-all-2",
    defaultValue: contentId,
    menuWidth: 300,
    onChange: onChangeTranslation,
    options: getTranslationOptions(),
    buttonUse: 'form',
    ButtonContent: makeButtonTextRenderer()
  });
  return /*#__PURE__*/_jsx("div", {
    className: className,
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "translationSelector.tooltip"
      }),
      placement: "bottom",
      children: translationSelector
    })
  });
};
export default TranslationSelectorContainer;
TranslationSelectorContainer.propTypes = {
  className: PropTypes.string
};